/* What the Access policy screen states, decided once and in one place.
 *
 * **The screen's whole subject is a disagreement between two sources**, so the judgements that decide
 * whether they agree live here and are tested without rendering. The bundle table is what this tenant
 * has *stored*; the running panel is what this *process* has instantiated, keyed on the content hash
 * of the bytes it is executing. `ActiveBundleRegistry` is a lazy cache by design — it is not
 * transactional and the publish is — so a freshly-activated bundle reads as absent until something
 * evaluates. CLAUDE.md §9 records that reading like a failure when it is not.
 */

import type { BundleVersion, LoadedBundle } from '@/shared/api/generated/policy';

/** How much of a hash to show. Enough to compare two by eye; the whole value goes in a `title`.
 *
 * The hash is the point of the running panel: it is what proves the process is executing the bytes
 * somebody reviewed, and `content_hash` is verified against the fetched artifact before anything is
 * instantiated precisely because object storage is not trusted to return what was written.
 */
export const HASH_VISIBLE = 12;

export function shortHash(hash: string): string {
  return hash.slice(0, HASH_VISIBLE);
}

/** The version a tenant has activated, or `undefined` when none is.
 *
 * `find` rather than a filter and an index, because "the active version" is singular by construction:
 * activating pins one and deprecates the previous. A second active row would be a substrate defect,
 * and this returning the first would hide it — so the caller that cares asserts the count instead.
 */
export function activeVersion(
  versions: readonly BundleVersion[],
): BundleVersion | undefined {
  return versions.find((version) => version.state === 'active');
}

/* ---------------------------------------------------------------- what is running

 * Whether the bytes a tenant activated are the bytes this process is executing.
 *
 * Three states, and the middle one is the reason this screen exists. `running` means the hashes match.
 * `not-instantiated` means the database says active and the registry has not filled — normal, not a
 * fault, and the copy says so. `none` means nothing is activated, so there is nothing to agree about;
 * collapsing it into `not-instantiated` would report an unconfigured policy as a lagging one.
 */
export type RunState = 'running' | 'not-instantiated' | 'none';

export function runStateOf(
  versions: readonly BundleVersion[],
  runningHashes: ReadonlySet<string>,
): RunState {
  const active = activeVersion(versions);
  if (active === undefined) return 'none';
  return runningHashes.has(active.content_hash) ? 'running' : 'not-instantiated';
}

/** The set of hashes this process is executing. A set because every row asks the same question of it,
 * and a nested `some` over the list would be quadratic in the number of bundles for no reason. */
export function runningHashesOf(loaded: readonly LoadedBundle[]): ReadonlySet<string> {
  return new Set(loaded.map((bundle) => bundle.content_hash));
}

/** The tone a run state takes. Total, so a fourth state is a compile error here rather than a chip
 * rendered in whatever hue a fallback picked — the defect §9 records for `verdictTone`, where a fifth
 * verdict silently took the *permitted* tone. */
export const RUN_TONE = {
  running: 'ok',
  'not-instantiated': 'warn',
  none: 'neutral',
} as const satisfies Record<RunState, 'ok' | 'warn' | 'neutral'>;

/* ---------------------------------------------------------------- one version's lifecycle

 * What a reader may do to a version, and why not when they may not.
 *
 * **`publish` only from draft, `reactivate` only from a deprecated version that has a semver.** That
 * second condition is not defensive: a draft has `semver: null` by design — an unpublished draft is
 * not something an activation can pin to, and a `0.0.0` placeholder would be a version number that
 * looks real — so a deprecated version without one has nothing the activate route could name.
 */
export type VersionAction = 'publish' | 'reactivate' | 'deprecate' | 'none';

/* ------------------------------------------------------------ is policy actually deciding anything

 * **The page's thesis, and the thing that was wrong.** This screen reported "Policy runtime
 * available" from `runtime_available`, which means only that the wasmtime wheel is installed — so
 * on a deployment with the wheel present and `LUMIQ_ABAC_ENABLED` unset it announced a capability
 * that decided nothing. A reader had no way to tell an enforcing deployment from an inert one.
 *
 * Five readings, because there are five genuinely different situations and each needs a different
 * action from a different person:
 *
 * * `no-engine` — an operator has to install a wasmtime build for this platform. Nothing a tenant
 *   does can take effect until they have.
 * * `abac-off` — **nobody has to do anything, and that is the correction.** This used to mean "an
 *   operator has forgot `LUMIQ_ABAC_ENABLED`", which made amber right; it now means this tenant
 *   decides access by role, which is a legitimate posture and the one most tenants are in. The copy
 *   distinguishes a *declared* off from one inherited from the deployment default, because those are
 *   different conversations with a customer.
 * * `on-without-policy` — scoping is switched on for this tenant and there is no governing bundle,
 *   so roles decide alone. Deliberate rather than a fault: refusing here would mean that switching
 *   scoping on before authoring a policy — the obvious order — locks every user out until the policy
 *   exists, with the recovery being the screen that has just been taken away. It is `warn` rather
 *   than `info` because it is a state somebody asked for and has not finished.
 * * `failing-closed` — urgent: a policy exists, cannot be honoured, and **every request is being
 *   refused**. An administrator has to publish or reactivate a version.
 * * `enforcing` — the healthy state, and it names the version so a reader can check it.
 *
 * **`no-policy` was a sixth member and is deleted rather than kept.** With scoping off answered
 * before the bundle state, and scoping on answered by `on-without-policy`, nothing could reach it —
 * so it would have read as a mechanism while being unreachable, which this codebase records as worse
 * than its absence.
 *
 * Derived here rather than in the component because it is the conclusion the whole screen exists
 * to state, and a conclusion assembled inline is one nothing can test.
 */
export type EnforcementReading =
  'no-engine' | 'abac-off' | 'on-without-policy' | 'failing-closed' | 'enforcing';

export interface EnforcementSummary {
  readonly reading: EnforcementReading;
  readonly tone: 'ok' | 'warn' | 'error' | 'info';
  readonly titleKey: string;
  readonly bodyKey: string;
}

/** Deployment facts first, then the tenant's own. The order is the point.
 *
 * A tenant with no bundle on a process with no engine has two problems, and naming the engine is
 * the one an operator can act on — so the deployment questions are answered before the tenant's.
 * Reversing it would report "no policy for this tenant" on a deployment where no tenant could have
 * one, which is true and useless.
 */
export function enforcementSummary(state: {
  readonly state: string;
  readonly runtime_available: boolean;
  readonly abac_enabled: boolean;
}): EnforcementSummary {
  if (!state.runtime_available) {
    return {
      reading: 'no-engine',
      tone: 'warn',
      titleKey: 'policy.enf.noEngine.title',
      bodyKey: 'policy.enf.noEngine.body',
    };
  }
  if (!state.abac_enabled) {
    /* **`info`, not `warn`, and this is the change that matters most on the page.** Scoping off used
     * to be a deployment misconfiguration — somebody had forgotten `LUMIQ_ABAC_ENABLED` — so amber
     * was right. It is now a setting a tenant chooses, and "RBAC only" is a legitimate posture that
     * most tenants are in; painting a chosen state as a warning is how a reader learns to ignore
     * this banner. The copy distinguishes a *declared* off from a defaulted one. */
    return {
      reading: 'abac-off',
      tone: 'info',
      titleKey: 'policy.enf.off.title',
      bodyKey: 'policy.enf.off.body',
    };
  }
  if (state.state === 'no_bundle') {
    return {
      reading: 'on-without-policy',
      tone: 'warn',
      titleKey: 'policy.enf.onNoPolicy.title',
      bodyKey: 'policy.enf.onNoPolicy.body',
    };
  }
  if (state.state === 'loaded') {
    return {
      reading: 'enforcing',
      tone: 'ok',
      titleKey: 'policy.enf.enforcing.title',
      bodyKey: 'policy.enf.enforcing.body',
    };
  }
  /* `no_active_version` and `load_failed` share a reading and **not** a sentence: both refuse every
     request, so the urgency is identical, and what an administrator does about it is not. The
     server's own `detail` names which, and the screen renders it verbatim rather than deciding. */
  return {
    reading: 'failing-closed',
    tone: 'error',
    titleKey: 'policy.enf.failing.title',
    bodyKey:
      state.state === 'no_active_version'
        ? 'policy.enf.failing.noVersion'
        : 'policy.enf.failing.loadFailed',
  };
}

/* ---------------------------------------------------------------- what decides a request

 * The tenant's authorization mode, stated rather than inferred.
 *
 * **The specification's requirement in one function**: ABAC off reads "RBAC only", ABAC on reads
 * "RBAC + Attribute Policy", and a reader is never asked to work that out from a banner or an
 * error. The server sends `mode` on `/enforcement` for exactly this, and this maps it onto a copy
 * key — so a screen cannot derive a third answer the server does not have.
 *
 * **Two values and not three.** "RBAC plus a policy that is currently refusing everything" is still
 * the second mode: the policy *is* deciding, and what it has decided is to refuse. A third value
 * there would let this screen report an enforcing tenant as unenforced on exactly the day it starts
 * refusing, which is the state that most needs to read as enforced.
 */
export type AuthorizationMode = 'rbac_only' | 'rbac_and_attribute_policy';

export const MODE_LABEL = {
  rbac_only: 'policy.mode.rbacOnly',
  rbac_and_attribute_policy: 'policy.mode.rbacAndPolicy',
} as const satisfies Record<AuthorizationMode, string>;

/** The copy key for a mode, refusing an unknown one rather than guessing.
 *
 * Total over the union with `satisfies`, so a third mode is a compile error here rather than a label
 * chosen by a fallback — the `verdictTone` defect §9 records, where a fifth verdict silently took
 * the *permitted* tone. An unrecognised string from a server ahead of this bundle falls to the
 * narrower claim, because reporting "RBAC only" for something we cannot read overstates nothing.
 */
export function modeLabelKey(mode: string): string {
  return mode === 'rbac_and_attribute_policy'
    ? MODE_LABEL.rbac_and_attribute_policy
    : MODE_LABEL.rbac_only;
}

/** Whether this tenant's setting is its own or inherited, as a copy key.
 *
 * **"Off because nobody has configured this" and "off because an administrator decided" are
 * different conversations with a customer**, and a screen that rendered them identically would
 * present a default as a choice — `federation`'s recorded mistake. `declared` is on the wire for
 * this and nothing else.
 */
export function postureOriginKey(posture: {
  readonly declared: boolean;
  readonly deployment_default: boolean;
}): string {
  if (posture.declared) return 'policy.abac.declared';
  return posture.deployment_default
    ? 'policy.abac.inheritedOn'
    : 'policy.abac.inheritedOff';
}

/* ---------------------------------------------------------------- can this bundle be deleted

 * **An active bundle is not deletable, and the screen must say so before the button is pressed.**
 *
 * Deleting a bundle whose version is deciding requests would change authorization as a side effect
 * of housekeeping — and for the governing bundle it would change it in the *widening* direction,
 * since a withdrawn `authz` reads as never-adopted and stops narrowing. The API refuses with a 409
 * and the exact sentence; this is what stops a reader having to discover that by pressing.
 *
 * `undefined` versions is **not** an empty list, for `agreementFor`'s reason: it means this screen
 * has not read them yet, and reporting a bundle as deletable on the strength of a pending request
 * would offer a control that is about to be refused.
 */
export type Deletability = 'deletable' | 'blocked-active' | 'unknown';

export function deletabilityOf(
  versions: readonly BundleVersion[] | undefined,
): Deletability {
  if (versions === undefined) return 'unknown';
  return activeVersion(versions) === undefined ? 'deletable' : 'blocked-active';
}

/** Why the Delete control is unavailable, as a copy key. `null` when it is available.
 *
 * Two distinct reasons rather than one disabled button with no explanation: "deprecate the active
 * version first" is a next step, and "still loading" is a wait.
 */
export function deleteBlockedKey(deletability: Deletability): string | null {
  if (deletability === 'blocked-active') return 'policy.delete.blockedActive';
  if (deletability === 'unknown') return 'policy.delete.blockedUnknown';
  return null;
}

/** Why an active version is not the one running, which depends on whether anything will ever load.
 *
 * **The wording this replaces was true of a mechanism that could not fire.** "The registry fills on
 * demand, so a freshly-activated bundle reads as absent until something evaluates" describes the
 * design correctly — and nothing called the loader, so it never filled, and a reader waited
 * indefinitely for a state that would never arrive. The loader is wired now, so the sentence is
 * accurate *when ABAC is on*; with ABAC off nothing will ever evaluate and it is still misleading.
 * So the reason is chosen from the flag rather than asserted.
 */
export function notLoadedReasonKey(abacEnabled: boolean): string {
  return abacEnabled ? 'policy.hash.notLoadedWhy' : 'policy.hash.notLoadedOff';
}

export function actionFor(version: BundleVersion): VersionAction {
  if (version.state === 'draft') return 'publish';
  if (version.state === 'deprecated' && semverOf(version) !== null) {
    return 'reactivate';
  }
  /* **`deprecate` on the active version, which is what makes `reactivate` above reachable.**
     Nothing in this UI could previously produce a deprecated version, so that branch was dead
     code -- a lifecycle the backend supported and the screen could not reach. */
  if (version.state === 'active' && semverOf(version) !== null) {
    return 'deprecate';
  }
  return 'none';
}

/** Why there is no action, as a copy key. Two different reasons, and a reader needs to know which:
 * "already the activated version" is the healthy case and "nothing to pin to" is a dead end. */
export function noActionKey(version: BundleVersion): string {
  return version.state === 'active'
    ? 'policy.noAction.active'
    : 'policy.noAction.unpinnable';
}

/* ---------------------------------------------------------------- do the two agree

 * One line per stored bundle: does what is activated match what is running.
 *
 * Derived here rather than in the component because it is the page's conclusion, and a conclusion
 * assembled inline is one nothing can test. The mark is a character *and* the note is a sentence —
 * a tick with no hash beside it is a claim a reader cannot check.
 */
export type Agreement = 'agrees' | 'lagging' | 'nothing-to-compare';

export interface AgreementRow {
  readonly bundleId: string;
  readonly bundleName: string;
  readonly agreement: Agreement;
  /** The active version's short hash, or `null` when there is no active version. */
  readonly hash: string | null;
}

export function agreementFor(
  bundle: { readonly id: string; readonly name: string },
  versions: readonly BundleVersion[] | undefined,
  runningHashes: ReadonlySet<string>,
): AgreementRow {
  /* **Undefined versions is not an empty list.** `undefined` means this screen has not read them yet
   * — the per-bundle read is still in flight or was capped — where `[]` means the bundle genuinely
   * has none. Reporting the first as "nothing to compare" would state a fact about the tenant's
   * configuration on the strength of a pending request. */
  if (versions === undefined) {
    return {
      bundleId: bundle.id,
      bundleName: bundle.name,
      agreement: 'nothing-to-compare',
      hash: null,
    };
  }
  const active = activeVersion(versions);
  if (active === undefined) {
    return {
      bundleId: bundle.id,
      bundleName: bundle.name,
      agreement: 'nothing-to-compare',
      hash: null,
    };
  }
  return {
    bundleId: bundle.id,
    bundleName: bundle.name,
    agreement: runningHashes.has(active.content_hash) ? 'agrees' : 'lagging',
    hash: shortHash(active.content_hash),
  };
}

/** The mark beside an agreement row. A character rather than an icon font, matching the reference, and
 * never the only signal — the note beside it says the same thing in words. */
export const AGREEMENT_MARK = {
  agrees: '✓',
  lagging: '○',
  'nothing-to-compare': '—',
} as const satisfies Record<Agreement, string>;

/* ---------------------------------------------------------------- the create form

 * Whether a bundle identity can be created, and the sentence under the form.
 *
 * Both fields are required, and the description is required *by this screen* rather than by the API,
 * which defaults it to empty. That is deliberate: the table's second column is "what it decides", and
 * a bundle with no answer there is a row a reader cannot act on. Requiring one line at creation is
 * cheaper than a screen full of blanks nobody can attribute.
 */
export interface BundleDraft {
  readonly name: string;
  readonly description: string;
}

export const EMPTY_BUNDLE_DRAFT: BundleDraft = { name: '', description: '' };

export function bundleDraftIsValid(draft: BundleDraft): boolean {
  return draft.name.trim().length > 0 && draft.description.trim().length > 0;
}

/** What the form's footer says: the blocking reason, or what creating it will and will not do.
 *
 * The third sentence is the one that matters, and it is why this form is not an upload: creating an
 * identity attaches no bytes and decides nothing. A reader who expected to be uploading a policy
 * needs to be told that here rather than discovering it from an empty versions table.
 */
export function bundleDraftHintKey(draft: BundleDraft): string {
  if (draft.name.trim().length === 0) return 'policy.form.needName';
  if (draft.description.trim().length === 0) return 'policy.form.needDescription';
  return 'policy.form.willCreate';
}

/** A version's number, or `null` when it has none.
 *
 * **Three spellings, one meaning.** `semver` is `string | null | undefined` on the wire — absent when
 * the field is omitted, `null` while DRAFT — and both mean "there is no version number to pin to".
 * Every caller reading `semver === null` would be right about drafts and wrong about the omitted case,
 * so the question is answered once here. `actionFor` already tested `typeof`, which is the same
 * judgement made in one branch rather than shared.
 */
export function semverOf(version: BundleVersion): string | null {
  return typeof version.semver === 'string' ? version.semver : null;
}

/** The semver of a bundle's active version, or `null` when nothing is active.
 *
 * **Read from the stored versions rather than from `/v1/policy/active`, and the difference is not
 * cosmetic.** That route reports what this *process* has instantiated, and the registry fills
 * lazily by design — so a bundle activated a moment ago reads as empty there. Seeding the builder
 * from it would open a blank form beside a policy the author had just activated, and they would
 * reasonably conclude their rules had been lost.
 *
 * `null` for a version whose semver is absent, which a draft's is: a draft has no number until it
 * is published, so it cannot be the thing the builder reopens.
 */
export function activeSemverOf(
  bundleId: string,
  versionsById: ReadonlyMap<string, readonly BundleVersion[]>,
): string | null {
  const versions = versionsById.get(bundleId);
  if (versions === undefined) return null;
  const active = versions.find((version) => version.state === 'active');
  return active?.semver ?? null;
}

/* ---------------------------------------------------------------- the enforcement band

 * **All five readings are shown at once, with the current one lit.** The most useful thing on the
 * redesigned band: it tells a reader that five states exist and which one they are in, so
 * `abac-off` reads as one posture among five rather than as an error to be interpreted. The old
 * screen showed one sentence and left the other four to be discovered by reaching them.
 *
 * Ordered from "nothing can enforce" to "enforcing", so the strip reads as a progression rather
 * than as an arbitrary row of chips.
 */
export const READING_ORDER: readonly EnforcementReading[] = [
  'no-engine',
  'abac-off',
  'on-without-policy',
  'failing-closed',
  'enforcing',
];

/** The band's accent, as a token name rather than a colour.
 *
 * **`--rail-*` and not `--status-*`.** The band sits on navy and the `--status-*` set is calibrated
 * for contrast on paper — `--status-error` on that ground is close to unreadable. These are the
 * same four meanings at the light end of the palette.
 *
 * Total over the tone union with `satisfies`, so a fifth tone is a compile error here rather than a
 * band rendered in whatever a fallback picked. That is the `verdictTone` defect §9 records, where a
 * fifth verdict silently took the *permitted* colour.
 */
export const RAIL_TONE = {
  ok: '--rail-ok',
  warn: '--rail-warn',
  error: '--rail-error',
  info: '--rail-info',
} as const satisfies Record<EnforcementSummary['tone'], string>;

export interface ReadingCell {
  readonly reading: EnforcementReading;
  readonly on: boolean;
  readonly labelKey: string;
  readonly tokenName: string;
}

/** The five cells, in order, with the current one marked.
 *
 * Derived from `READING_ORDER` rather than listed, so the strip cannot disagree with the union
 * about how many readings there are.
 */
export function readingStrip(current: EnforcementReading): readonly ReadingCell[] {
  return READING_ORDER.map((reading) => ({
    reading,
    on: reading === current,
    labelKey: `policy.reading.${reading}`,
    tokenName: RAIL_TONE[TONE_OF_READING[reading]],
  }));
}

/** Which tone each reading carries, kept beside `enforcementSummary` rather than re-derived.
 *
 * The strip needs a colour for readings that are *not* current, and calling `enforcementSummary`
 * five times with synthesised state to get them would be inventing four states that do not exist.
 */
const TONE_OF_READING = {
  'no-engine': 'warn',
  'abac-off': 'info',
  'on-without-policy': 'warn',
  'failing-closed': 'error',
  enforcing: 'ok',
} as const satisfies Record<EnforcementReading, EnforcementSummary['tone']>;

/* ---------------------------------------------------------------- the narrowing bars

 * **The page's signature element, and the one part of the redesign that is an argument rather than
 * a layout.** Two stacked bars: the role's grant at full width, and what attribute policy permits
 * *inside* it. The lower bar is never wider than the upper one — which is the product's central
 * asymmetry, stated in a shape instead of in a paragraph nobody reads.
 *
 * **The width when policy is not consulted is the decision worth defending.** Two wrong answers
 * were available and both are actively misleading:
 *
 * * **Full width** would read as "attribute policy permits everything" — a claim about a policy
 *   that is not running at all.
 * * **Zero width** would read as "nothing is permitted", when roles are permitting plenty.
 *
 * So a reading in which policy is not narrowing draws a **stub**: visibly not reaching across,
 * neutrally coloured, and captioned with what is actually happening. That is true — there is no
 * second bar to draw — and it is the absence-is-not-zero rule applied to a width.
 *
 * `failing-closed` is the same stub in the error tone, because a policy that refuses everything
 * lets nothing through: not reaching across is exactly the right picture.
 *
 * Total over the reading union, and the CSS caps the width at 100% besides — so no arrangement of
 * state can draw the policy wider than the grant it sits inside.
 */
export interface NarrowingBar {
  readonly width: string;
  readonly tone: 'neutral' | 'error' | 'enforcing';
  readonly captionKey: string;
}

export const NARROWING = {
  'no-engine': {
    width: '180px',
    tone: 'neutral',
    captionKey: 'policy.narrow.noEngine',
  },
  'abac-off': { width: '180px', tone: 'neutral', captionKey: 'policy.narrow.off' },
  'on-without-policy': {
    width: '180px',
    tone: 'neutral',
    captionKey: 'policy.narrow.onNoPolicy',
  },
  'failing-closed': {
    width: '180px',
    tone: 'error',
    captionKey: 'policy.narrow.failing',
  },
  /* Wide, and deliberately **not** 100%: a policy that narrowed nothing would be a policy with no
     effect, and the whole reason this bar exists is to show that it sits inside the grant. The
     figure is illustrative rather than measured — the platform does not compute what share of a
     grant a policy permits, and a number that looked measured would be a fabrication. The caption
     says so. */
  enforcing: { width: '62%', tone: 'enforcing', captionKey: 'policy.narrow.enforcing' },
} as const satisfies Record<EnforcementReading, NarrowingBar>;
